import { useEffect, useMemo, useRef, useState } from 'react';
import { useWizard } from 'react-use-wizard';
import Button from '@ramsey-design-system/button';
import {
  trackStepCompleted,
  trackStepErrorViewed
} from '../../../shared/analytics';
import { StepSpinner } from '../../../shared/components';
import { hasAlreadyFoundMatchFor, useMatcherContext } from '../context';
import { getAnsweredString } from '../flow/types';
import { buildMatchCriteria } from '../flow/buildMatchCriteria';
import {
  getMatcherLocationsFromZip,
  getMatcherFormName,
  useMatcherButtonSize,
  useTrackMatcherStepStarted
} from '../utils';
import { MatcherStandardStepTitle, MatcherStepWrapper } from '../components';
import { MatcherSearchFoundIcon } from '../icons/MatcherSearchFoundIcon';

const STEP_NAME = 'matcher_search_step';
const STEP_LABEL = 'We found a match!';

function formatLocation(city: string, region: string): string {
  return [city, region].filter(Boolean).join(', ');
}

function MatcherSearchStep() {
  const { nextStep, activeStep: wizardStep } = useWizard();
  const { matcherAnswers, matchSearchState, runMatchSearch } =
    useMatcherContext();
  const postalCode = getAnsweredString(matcherAnswers.postalCode);
  const [location, setLocation] = useState('');
  const buttonSize = useMatcherButtonSize();

  // A match was already found for these exact answers - e.g. the fan went back to an earlier
  // step and forward again without changing anything that feeds the search - so skip this step
  // entirely instead of re-showing a search that's already done. Requires 'success', not just
  // "not idle": a prior 'loading' or 'error' for this exact key means the search runMatchSearch
  // kicks off below is still pending or retrying, so the checklist needs to actually animate
  // through it rather than claim it's already done.
  const [skipStep] = useState(() =>
    hasAlreadyFoundMatchFor(matchSearchState, postalCode, matcherAnswers)
  );
  const [activeSpinIndex, setActiveSpinIndex] = useState(0);
  const [isFound, setIsFound] = useState(false);

  useTrackMatcherStepStarted(wizardStep + 1, STEP_NAME, STEP_LABEL, !skipStep);

  useEffect(() => {
    if (skipStep) {
      nextStep();
      return;
    }
    // No-ops if this exact search is already running or done - see runMatchSearch.
    runMatchSearch(postalCode, matcherAnswers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best-effort only, same fail-open pattern as matcherZipSchema's own lookup - a fan already
  // got past the Location step with this zip, so this is just re-resolving a display-friendly
  // city/state for it, not re-validating it.
  useEffect(() => {
    let isMounted = true;
    getMatcherLocationsFromZip(postalCode)
      .then((locations) => {
        if (!isMounted || locations.length === 0) return;
        setLocation(formatLocation(locations[0].city, locations[0].region));
      })
      .catch((e) => console.error(e));

    return () => {
      isMounted = false;
    };
  }, [postalCode]);

  // Tracking-only - no error UI here, the search is retried silently elsewhere.
  const previousStatus = useRef(matchSearchState.status);
  useEffect(() => {
    if (
      matchSearchState.status === 'error' &&
      previousStatus.current !== 'error'
    ) {
      trackStepErrorViewed({
        step: wizardStep + 1,
        step_name: STEP_NAME,
        step_label: STEP_LABEL,
        error_type: 'system',
        error_message: matchSearchState.errorMessage,
        form_name: getMatcherFormName()
      });
    }
    previousStatus.current = matchSearchState.status;
  }, [matchSearchState.status, matchSearchState.errorMessage, wizardStep]);

  const criteria = useMemo(
    () => buildMatchCriteria(matcherAnswers, location),
    [matcherAnswers, location]
  );

  const checklistLabels = [...criteria.spinLabels, criteria.revealLabel];

  function handleSpinComplete(spinIndex: number) {
    if (spinIndex === checklistLabels.length - 1) {
      setIsFound(true);
    } else {
      setActiveSpinIndex(spinIndex + 1);
    }
  }

  function handleContinue() {
    trackStepCompleted({
      step: wizardStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: [],
      answer_values: [],
      form_name: getMatcherFormName()
    });
    nextStep();
  }

  if (skipStep) {
    return null;
  }

  return (
    <MatcherStepWrapper hideProgressBar={true}>
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        {/* Cancels out MatcherStepWrapper's chevron-row mb-4 - only this step sits directly
        under an icon with nothing else (title/progress bar) needing that 32px gap. */}
        <MatcherSearchFoundIcon className="w-[80px] h-[80px] sm:w-[96px] sm:h-[96px] -mt-4" />
        {/* MatcherStandardStepTitle's noSubtitle margin is 32px, but this step needs 16px */}
        <div className="[&>p]:!mb-2">
          <MatcherStandardStepTitle noSubtitle>
            {isFound ? STEP_LABEL : 'Checking for matches . . .'}
          </MatcherStandardStepTitle>
        </div>
        <ul className="list-none p-0 m-0 mb-4 flex flex-col gap-2 items-start text-left">
          {checklistLabels.map((label, i) => (
            <li key={label} className="flex items-center gap-1">
              <div className="w-3.5 h-3.5 shrink-0">
                <StepSpinner
                  isActive={!isFound && i === activeSpinIndex}
                  onComplete={() => handleSpinComplete(i)}
                />
              </div>
              <span className="text-3 sm:text-4 text-gray-90">{label}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto md:mt-0">
        <Button
          type="button"
          className="!w-full"
          fullWidthOnMobile={true}
          size={buttonSize}
          data-testid="matcher_search_continue_button"
          disabled={!isFound}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </div>
    </MatcherStepWrapper>
  );
}

export { MatcherSearchStep };
